/**
 * API Utility for Bizify Dashboard
 * Base URL: http://185.237.15.202:8080/
 */

const BASE_URL = "https://api.bizifyerp.com/api";

const getHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
    };
};

export const api = {
    // Authentication
    registerCompany: async (data) => {
        const response = await fetch(`${BASE_URL}/Companyregisteration/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        return response.json();
    },

    login: async (data) => {
        // Note: User provided https://localhost:44315/api/Companyregisteration/login
        // Assuming this should be relative to the BASE URL in production context relative to the backend server IP provided.
        // The main server IP is http://185.237.15.202:8080/
        const response = await fetch(`${BASE_URL}/Companyregisteration/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        return response.json();
    },

    registerUser: async (data) => {
        const response = await fetch(`${BASE_URL}/Companyregisteration/register-user`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(data),
        });
        return response.json();
    },

    // Dashboard Data
    getTransactions: async (data) => {
        const queryParams = new URLSearchParams(data).toString();
        const response = await fetch(`${BASE_URL}/Dashboard/Transactions?${queryParams}`, {
            method: "GET",
            headers: getHeaders(),
        });
        return response.json();
    },

    getVouchers: async (data) => {
        // Fix potential double slash or missing slash if needed, though BASE_URL has no trailing slash usually.
        // User provided: https://api.bizifyerp.com/api/Dashboard/SearchVouchers
        const queryParams = new URLSearchParams(data).toString();
        const url = `${BASE_URL}/Dashboard/SearchVouchers?${queryParams}`;
        const response = await fetch(url, {
            method: "GET",
            headers: getHeaders(),
        });

        try {
            const text = await response.text();
            try {
                return JSON.parse(text);
            } catch (e) {
                console.warn(`JSON parse error for ${url}. Response text: "${text}"`, e);
                return { success: false, data: [] };
            }
        } catch (err) {
            console.error(`Network or Text error for ${url}`, err);
            return { success: false, data: [] };
        }
    },

    getBankCashList: async (data) => {
        // User provided: https://api.bizifyerp.com/api/Dashboard/SearchBank & Cash List
        // We use encodeURI to handle the space in "Bank & Cash List" or explicitly encode it
        const queryParams = new URLSearchParams(data).toString();
        // Construct URL carefully to match the weird endpoint name
        // "SearchBank & Cash List" -> encodeURIComponent or literal string if fetch handles it
        // The safest is to encode the components, but the user gave a raw URL string
        const endpoint = "/Dashboard/SearchBank%20&%20Cash%20List";
        const response = await fetch(`${BASE_URL}${endpoint}?${queryParams}`, {
            method: "GET",
            headers: getHeaders(),
        });
        return response.json();
    },

    getClosingCashBank: async (data) => {
        // User provided: https://api.bizifyerp.com/api/Dashboard/closing-cash-bank?companyCode=...&asOnDate=...
        const queryParams = new URLSearchParams(data).toString();
        const response = await fetch(`${BASE_URL}/Dashboard/closing-cash-bank?${queryParams}`, {
            method: "GET",
            headers: getHeaders(),
        });
        return response.json();
    },

    getOutstandList: async (data) => {
        // Endpoint: /Dashboard/Outstand List
        const queryParams = new URLSearchParams(data).toString();
        // Manually handle the space in "Outstand List" to be safe, though fetch/browser usually handles it.
        const endpoint = "/Dashboard/Outstand%20List";
        const response = await fetch(`${BASE_URL}${endpoint}?${queryParams}`, {
            method: "GET",
            headers: getHeaders(),
        });
        return response.json();
    },

    getCompanyDetails: async (data) => {
        const endpoints = [
            `${BASE_URL}/Companyregisteration/get-company-email?companyCode=${data.compcode}`,
            `${BASE_URL}/Companyregisteration/get-company?compcode=${data.compcode}`,
            `${BASE_URL}/Companyregisteration/GetCompany?compcode=${data.compcode}`,
            `${BASE_URL}/Companyregisteration/GetCompanyDetails?compcode=${data.compcode}`,
            `${BASE_URL}/Companyregisteration/company?compcode=${data.compcode}`,
            `${BASE_URL}/Companies/GetCompany?compcode=${data.compcode}`,
        ];

        let lastError = null;

        for (const url of endpoints) {
            try {
                const response = await fetch(url, {
                    method: "GET",
                    headers: getHeaders(),
                });

                if (response.ok) {
                    const text = await response.text();
                    if (text) {
                        try {
                            const result = JSON.parse(text);
                            // If success or has data, return it
                            if (result && (result.success || result.data || result.companyName || result.company_name)) {
                                console.log(`Successful company details fetch from: ${url}`);
                                // Unify return structure
                                return result.data ? result : { success: true, data: result };
                            }
                        } catch (e) {
                            console.warn(`JSON parse error for ${url}`, e);
                        }
                    }
                }
            } catch (err) {
                lastError = err;
                console.warn(`Network error for ${url}`, err);
            }
        }

        console.error("All getCompanyDetails attempts failed.");
        return { success: false, message: "Could not fetch company details", error: lastError };
    },

    // User Management
    // User Management
    getUsers: async (compCode, companyName) => {
        // HEURISTIC: Try multiple likely endpoints since documentation is unavailable.
        // Trying variations in Case, Controller names, and Query Parameters (Code vs Name)
        const endpoints = [
            // Standard - Code
            `${BASE_URL}/Companyregisteration/get-users?compcode=${compCode}`,
            `${BASE_URL}/Companyregisteration/get-users?CompCode=${compCode}`,

            // Standard - Name (New Heuristic based on user feedback)
            ...(companyName ? [
                `${BASE_URL}/Companyregisteration/get-users?name=${encodeURIComponent(companyName)}`,
                `${BASE_URL}/Companyregisteration/get-users?companyName=${encodeURIComponent(companyName)}`,
                `${BASE_URL}/Companyregisteration/get-users?CompanyName=${encodeURIComponent(companyName)}`,
            ] : []),

            // Variations
            `${BASE_URL}/Companyregisteration/GetAllUsers?compcode=${compCode}`,
            `${BASE_URL}/Companyregisteration/users?compcode=${compCode}`,
            `${BASE_URL}/Users/GetUsers?compcode=${compCode}`,
            `${BASE_URL}/Users?compcode=${compCode}`,
            `${BASE_URL}/Companyregisteration/get-users?CompanyCode=${compCode}`,
        ];

        let lastError = null;

        for (const url of endpoints) {
            try {
                const response = await fetch(url, {
                    method: "GET",
                    headers: getHeaders(),
                });

                if (response.ok) {
                    const text = await response.text();
                    if (text) {
                        try {
                            const data = JSON.parse(text);
                            // If we get an array or a success object with data array, return it.
                            if (Array.isArray(data)) {
                                console.log(`Successful user fetch from: ${url}`);
                                return data;
                            }
                            if (data && Array.isArray(data.data)) {
                                console.log(`Successful user fetch from: ${url}`);
                                return data.data;
                            }
                            if (data && Array.isArray(data.users)) {
                                console.log(`Successful user fetch from: ${url}`);
                                return data.users;
                            }
                        } catch (e) {
                            console.warn(`JSON parse error for ${url}`, e);
                        }
                    }
                }
            } catch (err) {
                lastError = err;
                console.warn(`Network error for ${url}`, err);
            }
        }

        // If all attempts fail
        console.error("All getUsers attempts failed.");
        return { success: false, message: "Could not fetch users", error: lastError };
    },
};
